import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Calendar, User, Clock, Tag, Search, Filter, ArrowRight, Bookmark, Share2, Heart } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { BlogPost } from '../types/database';

const Blog: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [featuredPost, setFeaturedPost] = useState<BlogPost | null>(null);

  useEffect(() => {
    document.title = 'Actualités | Proxy Emobility';
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
      // Set the first post as featured
      if (data && data.length > 0) {
        setFeaturedPost(data[0]);
      }
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', name: 'Tous les articles' },
    { id: 'technology', name: 'Technologie', color: 'bg-blue-100 text-blue-700' },
    { id: 'mobility', name: 'Mobilité', color: 'bg-green-100 text-green-700' },
    { id: 'sustainability', name: 'Développement Durable', color: 'bg-amber-100 text-amber-700' },
    { id: 'business', name: 'Business', color: 'bg-purple-100 text-purple-700' }
  ];

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filter === 'all' || post.category === filter;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getCategoryColor = (category: string) => {
    const cat = categories.find(c => c.id === category.toLowerCase());
    return cat?.color || 'bg-gray-100 text-gray-700';
  };

  const renderFeaturedPost = () => {
    if (!featuredPost) return null;

    return (
      <article className="relative bg-white rounded-2xl shadow-xl overflow-hidden mb-12 group">
        <div className="aspect-w-16 aspect-h-9 md:aspect-h-7">
          <img
            src={featuredPost.cover_image}
            alt={featuredPost.title}
            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/50 to-transparent" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
          <div className="flex items-center gap-4 mb-4">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(featuredPost.category)}`}>
              {featuredPost.category}
            </span>
            <span className="flex items-center text-sm">
              <Calendar size={16} className="mr-1" />
              {formatDate(featuredPost.created_at)}
            </span>
            <span className="flex items-center text-sm">
              <Clock size={16} className="mr-1" />
              {featuredPost.read_time} min
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4 group-hover:text-primary-300 transition-colors">
            {featuredPost.title}
          </h2>
          <p className="text-gray-200 text-lg mb-6 line-clamp-2">
            {featuredPost.excerpt}
          </p>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img
                src={featuredPost.author.avatar}
                alt={featuredPost.author.name}
                className="w-10 h-10 rounded-full object-cover border-2 border-white mr-3"
              />
              <div>
                <p className="font-medium">{featuredPost.author.name}</p>
                <p className="text-sm text-gray-300">Auteur</p>
              </div>
            </div>
            <Link
              to={`/blog/${featuredPost.slug}`}
              className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-lg transition-colors"
            >
              Lire l'article
              <ArrowRight size={18} className="ml-2" />
            </Link>
          </div>
        </div>
      </article>
    );
  };

  const renderArticleCard = (post: BlogPost) => (
    <article className="bg-white rounded-xl shadow-lg overflow-hidden transition-all hover:-translate-y-1 hover:shadow-xl group">
      <Link to={`/blog/${post.slug}`} className="block">
        <div className="relative">
          <img
            src={post.cover_image}
            alt={post.title}
            className="w-full h-48 object-cover transform group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute top-4 right-4 flex gap-2">
            <button className="p-2 bg-white/80 backdrop-blur-sm hover:bg-white rounded-full transition-colors">
              <Bookmark size={16} className="text-gray-700" />
            </button>
            <button className="p-2 bg-white/80 backdrop-blur-sm hover:bg-white rounded-full transition-colors">
              <Share2 size={16} className="text-gray-700" />
            </button>
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(post.category)}`}>
              {post.category}
            </span>
            <span className="flex items-center">
              <Clock size={16} className="mr-1" />
              {post.read_time} min
            </span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-primary-600 transition-colors">
            {post.title}
          </h2>
          <p className="text-gray-600 mb-4 line-clamp-3">
            {post.excerpt}
          </p>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img
                src={post.author.avatar}
                alt={post.author.name}
                className="w-8 h-8 rounded-full object-cover mr-2"
              />
              <div>
                <p className="text-sm font-medium text-gray-900">{post.author.name}</p>
                <p className="text-xs text-gray-500">{formatDate(post.created_at)}</p>
              </div>
            </div>
            <div className="flex items-center text-gray-500 text-sm">
              <button className="flex items-center hover:text-primary-600 transition-colors">
                <Heart size={16} className="mr-1" />
                <span>24</span>
              </button>
            </div>
          </div>
        </div>
      </Link>
    </article>
  );

  return (
    <div className="pt-20">
      <Helmet>
        <title>Blog et Actualités | Proxy Emobility</title>
        <meta name="description" content="Découvrez les dernières actualités sur la mobilité électrique en Afrique, nos innovations technologiques et nos engagements pour un avenir durable." />
        <meta name="keywords" content="blog mobilité électrique, actualités motos électriques, innovation technologique, développement durable, Afrique, batteries électriques, énergie solaire" />
        <link rel="canonical" href="https://proxyemobility.netlify.app/blog" />
        
        {/* Schema.org structured data for Blog */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Blog",
              "headline": "Blog et Actualités Proxy Emobility",
              "description": "Découvrez les dernières actualités sur la mobilité électrique en Afrique, nos innovations technologiques et nos engagements pour un avenir durable.",
              "url": "https://proxyemobility.netlify.app/blog",
              "publisher": {
                "@type": "Organization",
                "name": "Proxy Emobility",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://proxyemobility.netlify.app/proxy-logo.png"
                }
              },
              "blogPost": ${JSON.stringify(
                posts.slice(0, 5).map(post => ({
                  "@type": "BlogPosting",
                  "headline": post.title,
                  "description": post.excerpt,
                  "image": post.cover_image,
                  "url": `https://proxyemobility.netlify.app/blog/${post.slug}`,
                  "datePublished": post.created_at,
                  "author": {
                    "@type": "Person",
                    "name": post.author.name
                  }
                }))
              )}
            }
          `}
        </script>
      </Helmet>
      
      {/* Header */}
      <section className="bg-primary-900 text-white py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Actualités
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Découvrez les dernières actualités sur la mobilité électrique en Afrique, nos innovations et nos engagements pour un avenir durable.
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="py-8 bg-white border-b sticky top-20 z-10">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-lg">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher un article..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setFilter(category.id)}
                  className={`px-4 py-2 rounded-lg transition-all ${
                    filter === category.id
                      ? 'bg-primary-600 text-white scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          {loading ? (
            <div className="space-y-8">
              <div className="h-96 bg-gray-200 rounded-2xl animate-pulse" />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-xl shadow-lg overflow-hidden animate-pulse">
                    <div className="h-48 bg-gray-200" />
                    <div className="p-6 space-y-4">
                      <div className="h-4 bg-gray-200 rounded w-1/4" />
                      <div className="h-8 bg-gray-200 rounded w-3/4" />
                      <div className="h-4 bg-gray-200 rounded w-full" />
                      <div className="h-4 bg-gray-200 rounded w-2/3" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <Filter size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucun article trouvé</h3>
              <p className="text-gray-600">
                Essayez de modifier vos critères de recherche
              </p>
            </div>
          ) : (
            <div className="space-y-16">
              {/* Featured Post */}
              {filter === 'all' && renderFeaturedPost()}

              {/* Regular Posts Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.filter(post => post.id !== featuredPost?.id).map(renderArticleCard)}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Restez Informé
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Abonnez-vous à notre newsletter pour recevoir les dernières actualités et mises à jour.
            </p>
            <form className="flex gap-2">
              <input
                type="email"
                placeholder="Votre adresse email"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                type="submit"
                className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                S'abonner
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;